import { observe, toJS, when } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as Moment from 'moment-timezone';
import * as React from 'react';
import * as Datetime from 'react-datetime';
import { Prompt, Redirect, withRouter } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Label, Row } from 'reactstrap';
import { EInput } from 'src/Components/EInput';
import { SyncStore } from 'src/Stores/Sync/SyncStore';
import { TestsStore } from 'src/Stores/Tests/TestsStore';
import { UserStore } from 'src/Stores/User/UserStore';
import { WellsStore } from 'src/Stores/Wells/WellsStore';
import '../../../../../../node_modules/react-datetime/css/react-datetime.css';
import { ITest } from '../../../../../Interfaces/TestInterface';
import { IWell } from '../../../../../Interfaces/WellInterface';
import DataService from '../../../../../Services/Data';
import Footer from '../../../../Footer';
import WorkflowHeader from '../../../WorkflowHeader';
import CopyTestDataModal from '../CopyTestDataModal/CopyTestDataModal';

const service = new DataService();

@inject('stores')
@observer
class TestSetup extends React.Component {
    public props: any;
    public TestsStore: TestsStore;
    private test: ITest = new ITest();
    private testDisposer: any;
    public WellsStore: WellsStore;
    private well: IWell = new IWell();
    private UserStore: UserStore;
    private SyncStore: SyncStore;

    public state = {
        copyModal: false,
        formIsValid: false,
        isDirty: false,
        mudTypes: [],
        newTest: false,
        propertyTypes: [],
        taskId: 'new',
        test: this.test,
        well: this.well
    };

    constructor(props: any) {
        super(props);

        Moment.locale('en');
        this.TestsStore = this.props.stores.TestsStore;
        this.WellsStore = this.props.stores.WellsStore;
        this.UserStore = this.props.stores.UserStore;
        this.SyncStore = this.props.stores.SyncStore;

        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleDropdownChange = this.handleDropdownChange.bind(this);
        this.handleDateTimeChange = this.handleDateTimeChange.bind(this);
        this.handleRadioSelect = this.handleRadioSelect.bind(this);

        this.clearDisposers = this.clearDisposers.bind(this);
    }

    public componentDidMount() {
        this.createDisposers();
        this.setState({
            saveClicked: false,
            taskId: this.props.match.params.taskId,
        });
        if ( ! this.WellsStore.well ) {
            this.WellsStore.getWellDetails(this.props.match.params.wellId);
        }

        if ( this.props.match.params.taskId !== 'new' ) {
            this.TestsStore.getTestDetails(this.props.match.params.taskId, this.props.match.params.wellId);
            this.setState({
                newTest:false
            });
        } else {
            this.TestsStore.createNewTestInstance();
            this.setState({
                newTest: true,
            });
        }
        this.getMudTypes();
    }

    public createDisposers() {
        this.testDisposer = observe(this.TestsStore, 'test', (change: any) => {
            const test: ITest = toJS(change.newValue);
            when(() => this.WellsStore.well !== undefined).then(() => {
                if (test.testDate) {
                    test.preTestDate = Moment.unix(test.testDate).tz(this.WellsStore.well.timeZoneName);
                } else {
                    test.wellId = this.props.match.params.wellId;
                    test.preTestDate = Moment.tz(Date.now(), this.WellsStore.well.timeZoneName);
                }
                if ( test.mudProperty ) {
                    const filteredList = service.search(
                        this.state.mudTypes,
                        test.mudProperty,
                        ['typeName']
                    );
                    if ( filteredList && filteredList.length > 0 ) {
                        this.setState({
                            propertyTypes: filteredList[0].subTypeNames
                        });
                    }
                }
                this.setState({
                    test
                });
                return change;
            });
        });
    }

    public componentWillReceiveProps(nextProps: any, prevProps: any) {
        this.setState({
            taskId: this.props.match.params.taskId
        });
        if (this.props.match.params.taskId !== nextProps.match.params.taskId) {
            if ( nextProps.match.params.taskId !== 'new' ) {
                this.TestsStore.getTestDetails(nextProps.match.params.taskId, this.props.match.params.wellId);
                this.setState({newTest: false});
            } else {
                this.TestsStore.createNewTestInstance();
                this.setState({
                    newTest: true,
                });
            }
        }
    }

    public componentWillUnmount() {
        this.clearDisposers();
    }

    public clearDisposers() {
        this.testDisposer();
    }

    private getMudTypes() {
        this.TestsStore.getMudTypes()
        .then(
            (data: any) => {
                this.setState({
                    mudTypes: data
                });
            }
        );
    }

    private handleInputChange(event: any) {
        const test = this.state.test;
        test[event.target.name] = event.target.value;
        this.setState({
            isDirty: true,
            test
        });
    }

    private handleDropdownChange(event: any) {
        const index = event.target.selectedIndex;
        const mudType: any = this.state.mudTypes[index - 1];
        const test = this.state.test;
        test.mudProperty = event.target.value;
        test.propertyType = '';
        test.weighted = '';
        this.setState({
            isDirty: true,
            propertyTypes: mudType.subTypeNames,
            test
        });
    }

    private handleDateTimeChange(value: any) {
        const test = this.state.test;
        test.preTestDate = value;
        this.setState({
            isDirty: true,
            test
        });
    }

    private handleRadioSelect(event: any) {
        const test = this.state.test;
        const inventory = event.target.value;
        test[event.target.name] = (inventory === 'true');
        this.setState({
            [event.target.name]: (inventory === 'true'),
            isDirty: true,
            test
        });
    }

    private handleTestDelete = () => {
        const areYouSure = confirm("Deleting a Well Test permanently removes it from the system. Are you sure you want to delete this Well Test?\n\nPress Cancel to avoid deleting the Test OR press OK to permanently delete the Test.");
        if (areYouSure) {
            this.TestsStore.deleteTest(this.state.test.id, this.WellsStore.well.id)
            .then(data => {
                if (data) {
                    this.props.history.replace(`/well/${this.WellsStore.well.id}/details`);
                }
            });
        }
    }
    private updateTestModel = () => {
        this.TestsStore.updateTestModel(this.state.test, this.props.match.params.wellId).then((test: any) => {
            if (test) {
                if (this.state.newTest) {
                    this.setState({
                        isDirty: false,
                        newTest: false,
                    }, () => {
                        this.props.history.push('/well/' + this.props.match.params.wellId + '/test/' + test.id + '/welldata/testsetup');
                    });
                } else {
                    this.setState({
                        isDirty: false,
                    });
                }
            }
        });
    }

    private showWeightedRadio = () => {
        return this.state.test.mudProperty === 'Water Based Mud'
            && this.state.test.propertyType !== 'Brine Emulsion';
    }

    private weightedNotSelected = () => {
        return (this.state.test.weighted === undefined 
        || this.state.test.weighted === ''
        || this.state.test.weighted === null);
    }

    private volAcctRepNotSelected = () => {
        return (this.state.test.isVolumeAccountingReport === undefined);
    }

    private inventoryRepNotSelected = () => {
        return (this.state.test.isInventoryReport === undefined);
    }

    private newIntervalRepNotSelected = () => {
        return (this.state.test.isNewIntervalReport === undefined);
    }

    private depthBitInvalid = () => {
        return Number(this.state.test.depthBit) > Number(this.state.test.depthMD);
    }

    private depthTvdInvalid = () => {
        return Number(this.state.test.depthTVD) > Number(this.state.test.depthMD);
    }

    private mudReportDateInvalid = () => {
        if (this.WellsStore.well && this.TestsStore.tests && this.TestsStore.tests[0] && this.state.test.id === '') {
            const prevTest = this.TestsStore.tests[0];
            if (Moment.unix(prevTest.testDate).tz(this.WellsStore.well.timeZoneName) > this.state.test.preTestDate) {
                return true;
            }
        }
        return false;
    }

    private saveGrey = () => {
        return (this.showWeightedRadio() && this.weightedNotSelected())
            || this.depthBitInvalid()
            || this.volAcctRepNotSelected()
            || this.depthTvdInvalid() 
            || this.inventoryRepNotSelected()
            || this.newIntervalRepNotSelected()
            || this.mudReportDateInvalid();
    }

    private buttonNavigation = (event: any) => {
        const $target = event.target;

        if ( $target.name === 'cancel' ) {
            this.props.history.push('/home');
        } else if ($target.name === 'previous' ) {
            this.props.history.go(-1);
        } else {
            this.props.history.push('/well/' + this.props.match.params.wellId + '/test/' + this.state.test.id + '/welldata/managecontacts');
        }
    }

    
    private canDeleteTest = () => {
        const genericPermission = this.UserStore.hasGenericPermission('Permissions.WellTests.Delete');
        const deleteableTest = this.TestsStore.canDelete() && this.WellsStore.well && this.WellsStore.well.wellStatus === 'Active';
        if (genericPermission && deleteableTest) {
            if (this.UserStore.hasPermission('Permissions.WellTests.Delete.All')) {
                return true;
            } else {
                return this.WellsStore.well.engineers.some(eng => eng.id === this.UserStore.user.id);
            }
        }
        return false;
    }

    private redirectToMostRecentTest = () => {
        if (this.WellsStore.well 
            && this.TestsStore.test
            && this.TestsStore.test.id === ''
            && this.WellsStore.well.id === this.props.match.params.wellId
            && this.TestsStore.tests
            && this.TestsStore.tests[0]
            && this.TestsStore.tests[0].testStatus !== 'Approved') {
            if (this.UserStore.hasGenericPermission('Permissions.WellTests.Approve')) {
                if (!this.UserStore.hasPermission('Permissions.WellTests.Approve.All')) {
                    return !this.WellsStore.well.engineers.some(eng => eng.id === this.UserStore.user.id);
                }
            } else {
                return true;
            }
        }
        return false;
    }

    private toggleCopyModal = () => {
        this.setState({
            copyModal: !this.state.copyModal
        });
    }

    public render() {
        const canDelete = this.canDeleteTest();
        return(
            <div className="TestSetup">
                {
                    this.redirectToMostRecentTest() &&
                    <Redirect to={`/well/${this.WellsStore.well.id}/test/${this.TestsStore.tests[0].id}/welldata/testsetup`} />
                }
                <Prompt when={this.state.isDirty} message={"Changes have been made.\n\nPress Cancel then Press Save OR Press OK to continue without saving."} />
                <WorkflowHeader workflowHeaderItems={this.props.workflowHeaderItems} />
                <Form>
                    <Row>
                        <Col md="3" className="radios input-error">
                            <Label className="required">Is this an inventory report?</Label><br/>
                            <FormGroup check={true} inline={true}>
                                <Label check={true}>
                                    <EInput
                                        name="isInventoryReport"
                                        id="isInventoryReport"
                                        type="radio"
                                        value="true"
                                        required={true}
                                        disabled={this.state.test.id !== ''}
                                        onChange={this.handleRadioSelect}
                                        checked={this.state.test.isInventoryReport}
                                    /> Yes
                                </Label>
                            </FormGroup>
                            <FormGroup check={true} inline={true}>
                                <Label check={true}>
                                    <EInput
                                        name="isInventoryReport"
                                        id="isInventoryReport"
                                        type="radio"
                                        value="false"
                                        required={true}
                                        disabled={this.state.test.id !== ''}
                                        onChange={this.handleRadioSelect}
                                        checked={this.state.test.isInventoryReport === false}
                                    /> No
                                </Label>
                            </FormGroup>
                            {/* Removed By Sam as of 2/25/2020 {
                            this.inventoryRepNotSelected() &&
                            <div className="error-messages">
                                <p className="validationError">This field is a required field</p>
                            </div>
                            } */}
                        </Col>
                        <Col md="3" className="radios input-error">
                            <Label className="required">Is this a volume accounting report?</Label><br/>
                            <FormGroup check={true} inline={true}>
                                <Label check={true}>
                                    <EInput
                                        name="isVolumeAccountingReport"
                                        id="isVolumeAccountingReport"
                                        type="radio"
                                        value="true"
                                        required={true}
                                        disabled={this.state.test.id !== ''}
                                        onChange={this.handleRadioSelect}
                                        checked={this.state.test.isVolumeAccountingReport}
                                    /> Yes
                                </Label>
                            </FormGroup>
                            <FormGroup check={true} inline={true}>
                                <Label check={true}>
                                    <EInput
                                        name="isVolumeAccountingReport"
                                        id="isVolumeAccountingReport"
                                        type="radio"
                                        value="false"
                                        required={true}
                                        disabled={this.state.test.id !== ''}
                                        onChange={this.handleRadioSelect}
                                        checked={this.state.test.isVolumeAccountingReport === false}
                                    /> No
                                </Label>
                            </FormGroup>
                            {/* {
                            this.volAcctRepNotSelected() &&
                            <div className="error-messages">
                                <p className="validationError">This field is a required field</p>
                            </div>
                            } */}
                        </Col>
                        <Col md={this.showWeightedRadio() ? '3' : '6'} className="radios input-error">
                            <Label className="required">Is this a new interval report?</Label><br/>
                            <FormGroup check={true} inline={true}>
                                <Label check={true}>
                                    <EInput
                                        name="isNewIntervalReport"
                                        id="isNewIntervalReport"
                                        type="radio"
                                        value="true"
                                        required={true}
                                        disabled={this.state.test.id !== ''}
                                        onChange={this.handleRadioSelect}
                                        checked={this.state.test.isNewIntervalReport}
                                    /> Yes
                                </Label>
                            </FormGroup>
                            <FormGroup check={true} inline={true}>
                                <Label check={true}>
                                    <EInput
                                        name="isNewIntervalReport"
                                        id="isNewIntervalReport"
                                        type="radio"
                                        value="false"
                                        required={true}
                                        disabled={this.state.test.id !== ''}
                                        onChange={this.handleRadioSelect}
                                        checked={this.state.test.isNewIntervalReport === false}
                                    /> No
                                </Label>
                            </FormGroup>
                            {/* {
                            this.newIntervalRepNotSelected() &&
                            <div className="error-messages">
                                <p className="validationError">This field is a required field</p>
                            </div>
                            } */}
                        </Col>
                        {
                            this.showWeightedRadio() && 
                        <Col md="3" className="radios input-error">
                            <Label className="required">Is this a Weighted Drilling Fluid?</Label><br/>
                            <FormGroup check={true} inline={true}>
                                <Label check={true}>
                                    <EInput
                                        name="weighted"
                                        id="weighted"
                                        type="radio"
                                        value="true"
                                        onChange={this.handleRadioSelect}
                                        required={true}
                                        disabled={this.state.test.testStatus === 'Approved'}
                                        checked={this.state.test.weighted}
                                    /> Yes
                                </Label>
                            </FormGroup>
                            <FormGroup check={true} inline={true}>
                                <Label check={true}>
                                    <EInput
                                        name="weighted"
                                        id="weighted"
                                        type="radio"
                                        value="false"
                                        onChange={this.handleRadioSelect}
                                        required={true}
                                        disabled={this.state.test.testStatus === 'Approved'}
                                        checked={this.state.test.weighted === false}
                                    /> No
                                </Label>
                            </FormGroup>
                            {/* {
                                this.showWeightedRadio() && 
                                this.weightedNotSelected() &&
                            <div className="error-messages">
                                <p className="validationError">This field is a required field</p>
                            </div>
                            } */}
                        </Col>
                        }
                        <Col md="6">
                            <FormGroup className="required">
                                <Label>Date</Label>
                                <Datetime
                                    value={this.state.test.preTestDate}
                                    onChange={this.handleDateTimeChange}
                                    inputProps={{disabled: this.state.test.id !== ''}}
                                />
                            {
                                this.mudReportDateInvalid() &&
                                <div className="input-error">
                                    <div className="error-messages">
                                        <p className="validationError">This well test must be at a later date/time than the previous to create.</p>
                                    </div>
                                </div>
                            }
                            </FormGroup>
                        </Col>
                        <Col md="6" />
                        <Col md="4">
                            <FormGroup className="required">
                                <Label>Depth MD (ft)</Label>
                                <EInput
                                    name="depthMD"
                                    id="depthMD"
                                    data-validation="integer"
                                    required={true}
                                    type="text"
                                    value={this.state.test.depthMD}
                                    onChange={this.handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup className="required">
                                <Label>Depth TVD (ft)</Label>
                                <EInput
                                    name="depthTVD"
                                    id="depthTVD"
                                    data-validation="integer"
                                    required={true}
                                    type="text"
                                    value={this.state.test.depthTVD}
                                    onChange={this.handleInputChange}
                                    errorCallback={{
                                        callback: this.depthTvdInvalid,
                                        message: 'The Depth TVD value is greater than the Depth MD of this Test. Please update your Depth TVD before saving.'
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup className="required">
                                <Label>Bit Depth (ft)</Label>
                                <EInput
                                    name="depthBit"
                                    id="depthBit"
                                    data-validation="integer"
                                    required={true}
                                    type="text"
                                    value={this.state.test.depthBit}
                                    onChange={this.handleInputChange}
                                    errorCallback={{
                                        callback: this.depthBitInvalid,
                                        message: 'The Bit Depth value is greater than the Depth MD of this Test. Please update your Bit Depth before saving.'
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="12">
                            <FormGroup className="required">
                                <Label>Present Activity</Label>
                                <EInput
                                    name="presentActivity"
                                    id="presentActivity"
                                    required={true}
                                    type="textarea"
                                    value={this.state.test.presentActivity}
                                    onChange={this.handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="required">
                                <Label>Report Type</Label>
                                <EInput
                                    name="mudProperty"
                                    id="mudProperty"
                                    required={true}
                                    type="select"
                                    value={this.state.test.mudProperty}
                                    disabled={this.state.test.id !== ''}
                                    onChange={this.handleDropdownChange}
                                >
                                    <option disabled={true} />
                                    { this.state.mudTypes && this.state.mudTypes.map((mudType: any, index: number) =>
                                        <option key={index} value={mudType.typeName}>{mudType.typeName}</option>
                                    ) }
                                </EInput>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="required">
                                <Label>Mud Type</Label>
                                <EInput
                                    name="propertyType"
                                    id="propertyType"
                                    required={true}
                                    type="select"
                                    value={this.state.test.propertyType}
                                    onChange={this.handleInputChange}
                                >
                                    <option disabled={true} />
                                    { this.state.propertyTypes && this.state.propertyTypes.map((property: string, index: number) =>
                                        <option key={index} value={property}>{property}</option>
                                    ) }
                                </EInput>
                            </FormGroup>
                        </Col>
                    </Row>
                </Form>
                {
                    canDelete &&
                    <Row style={{marginBottom: '1rem'}}>
                        <Col>
                            <Button type='button' color='danger' onClick={this.handleTestDelete}>Delete Test</Button>
                        </Col>
                    </Row>
                }
                {
                    this.TestsStore.test &&
                    this.TestsStore.tests &&
                    this.TestsStore.tests.length === 1 &&
                    this.TestsStore.test.id === this.TestsStore.tests[0].id &&
                    !this.SyncStore.offline &&
                    <Row>
                        <Col>
                            <Button type='button' color='primary' onClick={this.toggleCopyModal}>Copy Data From Another Well</Button>
                        </Col>
                    </Row>
                }
                <Footer newTest={this.state.newTest} well={this.WellsStore.well} test={this.TestsStore.test} onSave={this.updateTestModel} onNavigate={this.buttonNavigation} saveGrey={this.saveGrey()} />
                {
                    !this.SyncStore.offline &&
                    <CopyTestDataModal open={this.state.copyModal} toggle={this.toggleCopyModal} />
                }
            </div>
        );
    }
}

export default withRouter(TestSetup);



// WEBPACK FOOTER //
// ./src/Components/Main/TestWorkflow/WellData/TestSetup/TestSetup.tsx